import { shouldLaunchWithFreshSession } from '../components/layout/browserLaunchSession';
import { useProjectStore } from '../store/projectStore';
import { useProxyStore } from '../store/proxy-store';
import { resolveApplicationStartUrl } from '@terra/shared-asset-processing';
import { ensureProxyRunningForPortalTools } from './ensureProxyRunningForPortalTools';
import { readProxyStatusSafely } from './readProxyStatusSafely';
import { injectProxiedBrowserCredentials } from './injectProxiedBrowserCredentials';
import { registerTorchTrackingBrowserIfApplicable } from './registerTorchTrackingBrowser';

type LaunchProxiedBrowserParams = {
  startUrl?: string;
  instanceId?: string;
  credentialId?: string;
  freshSession?: boolean;
  applicationId?: string;
  stealthMode?: boolean;
};

type LaunchProxiedBrowserResult =
  | {
      success: true;
      instanceId?: string;
      pageId?: string;
      startUrl?: string;
      navigatedUrl?: string;
      focusedExisting: boolean;
      credentialInjected?: boolean;
    }
  | {
      success: false;
      error: string;
    };

async function syncMtlsSettings(): Promise<{
  success: boolean;
  error?: string;
}> {
  const { settings } = useProxyStore.getState();
  const enabledSettingsEntries = settings.clientTlsCertificates.filter(
    (entry) => entry.enabled,
  );

  const mtlsSyncResult = await window.electronAPI.proxy.setMtlsConfig(
    enabledSettingsEntries.map((entry) => ({
      id: entry.id,
      origin: entry.origin,
      cert: entry.cert ?? null,
      key: entry.key ?? null,
      pfx: entry.pfx ?? null,
      passphrase: entry.passphrase ?? null,
      source: entry.source,
      enabled: true,
    })),
  );

  if (!mtlsSyncResult.success) {
    return {
      success: false,
      error: mtlsSyncResult.error || 'Failed to sync mTLS settings',
    };
  }

  return { success: true };
}

export async function launchProxiedBrowser({
  startUrl,
  instanceId,
  credentialId,
  freshSession,
  applicationId,
  stealthMode,
}: LaunchProxiedBrowserParams = {}): Promise<LaunchProxiedBrowserResult> {
  const { settings, isProxyRunning, setProxyRunning } =
    useProxyStore.getState();
  const selectedTarget = useProjectStore.getState().selectedTarget;
  const selectedCompany = useProjectStore.getState().selectedCompany;
  const resolvedApplicationId = applicationId ?? selectedTarget?.id;

  if (typeof window === 'undefined' || !window.electronAPI) {
    return {
      success: false,
      error: 'Electron API is not available',
    };
  }

  const realStatus = await readProxyStatusSafely();
  const proxyIsRunning = realStatus?.isRunning ?? isProxyRunning;

  if (proxyIsRunning !== isProxyRunning) {
    setProxyRunning(proxyIsRunning);
  }

  if (!proxyIsRunning) {
    const startResult = await ensureProxyRunningForPortalTools();

    if (!startResult.success) {
      return {
        success: false,
        error: startResult.error,
      };
    }
  }

  const scopedApplicationUrl = selectedTarget
    ? resolveApplicationStartUrl({
        applicationUrl: selectedTarget.url,
        domains: selectedTarget.domains ?? [],
      })
    : undefined;
  const resolvedStartUrl = startUrl ?? scopedApplicationUrl;

  if (instanceId) {
    const openTabResult = await window.electronAPI.browser.openTab({
      instanceId,
      url: resolvedStartUrl,
    });

    if (!openTabResult.success) {
      return {
        success: false,
        error: openTabResult.error || 'Failed to open tab in browser instance',
      };
    }

    registerTorchTrackingBrowserIfApplicable({
      browserId: instanceId,
      applicationId: resolvedApplicationId,
    });

    let credentialInjected = false;

    if (credentialId && resolvedApplicationId) {
      const injectResult = await injectProxiedBrowserCredentials({
        browserId: instanceId,
        credentialId,
        applicationId: resolvedApplicationId,
        applicationUrl: scopedApplicationUrl ?? selectedTarget?.url,
        startUrl: resolvedStartUrl,
      });

      if (!injectResult.success) {
        return {
          success: false,
          error:
            injectResult.error || 'Failed to inject credentials into browser',
        };
      }

      credentialInjected = true;
    }

    return {
      success: true,
      instanceId,
      pageId: openTabResult.data?.pageId,
      startUrl: resolvedStartUrl,
      navigatedUrl: resolvedStartUrl,
      focusedExisting: true,
      credentialInjected,
    };
  }

  const mtlsSyncResult = await syncMtlsSettings();

  if (!mtlsSyncResult.success) {
    return {
      success: false,
      error: mtlsSyncResult.error || 'Failed to sync mTLS settings',
    };
  }

  const enabledSettingsEntries = settings.clientTlsCertificates.filter(
    (entry) => entry.enabled,
  );

  const launchResult = await window.electronAPI.browser.launchChrome({
    proxyHost: settings.host,
    proxyPort: settings.port,
    freshSession: freshSession ?? shouldLaunchWithFreshSession(),
    startUrl: resolvedStartUrl,
    tenantId: selectedCompany?.tenantId ?? undefined,
    applicationId: resolvedApplicationId,
    extensionsEnabled: settings.extensionsEnabled,
    stealthMode,
    clientCertificates: enabledSettingsEntries.map((entry) => ({
      origin: entry.origin,
      cert: entry.cert,
      key: entry.key,
      pfx: entry.pfx,
      passphrase: entry.passphrase,
    })),
  });

  if (!launchResult.success) {
    return {
      success: false,
      error: launchResult.error || 'Failed to launch browser',
    };
  }

  registerTorchTrackingBrowserIfApplicable({
    browserId: launchResult.instanceId,
    applicationId: resolvedApplicationId,
  });

  const focusedExisting = launchResult.focusedExisting ?? false;
  let credentialInjected = false;

  if (credentialId && resolvedApplicationId && launchResult.instanceId) {
    const injectResult = await injectProxiedBrowserCredentials({
      browserId: launchResult.instanceId,
      credentialId,
      applicationId: resolvedApplicationId,
      applicationUrl: scopedApplicationUrl ?? selectedTarget?.url,
      startUrl: resolvedStartUrl,
    });

    if (!injectResult.success) {
      return {
        success: false,
        error:
          injectResult.error || 'Failed to inject credentials into browser',
      };
    }

    credentialInjected = true;
  }

  return {
    success: true,
    instanceId: launchResult.instanceId,
    pageId: launchResult.pageId,
    startUrl: resolvedStartUrl,
    navigatedUrl: resolvedStartUrl,
    focusedExisting,
    credentialInjected,
  };
}
